import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Save, Loader2 } from 'lucide-react';

const WORK_TYPES = [
  { value: 'dev', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'support', label: 'Support' },
  { value: 'admin', label: 'Admin' },
  { value: 'learning', label: 'Learning' },
  { value: 'other', label: 'Other' },
];

export function TimesheetEntryDialog({
  open, onOpenChange, editingEntry, entryForm, setEntryForm,
  onSave, saving, projects, weekData
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Timesheet Entry'}</DialogTitle>
          <DialogDescription>Log hours for a specific task on a given day.</DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={entryForm.date}
                onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                min={weekData?.week_start}
                max={weekData?.week_end}
                data-testid="entry-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Hours</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={entryForm.hours_spent}
                onChange={(e) => setEntryForm({ ...entryForm, hours_spent: parseFloat(e.target.value) || 0 })}
                data-testid="entry-hours-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Project</Label>
            <Select
              value={entryForm.project_id}
              onValueChange={(v) => setEntryForm({ ...entryForm, project_id: v })}
            >
              <SelectTrigger data-testid="entry-project-select">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Task / Description</Label>
            <Input
              value={entryForm.task_id || entryForm.description}
              onChange={(e) => setEntryForm({ ...entryForm, task_id: e.target.value, description: e.target.value })}
              placeholder="Task name or description"
              data-testid="entry-task-input"
            />
            {entryForm.devops_work_item_id && (
              <p className="text-xs text-muted-foreground">
                Linked to DevOps #{entryForm.devops_work_item_id}
              </p>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Work Type</Label>
              <Select
                value={entryForm.work_type}
                onValueChange={(v) => setEntryForm({ ...entryForm, work_type: v })}
              >
                <SelectTrigger data-testid="entry-worktype-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORK_TYPES.map(wt => (
                    <SelectItem key={wt.value} value={wt.value}>{wt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Billable</Label>
              <div className="flex items-center h-10">
                <Switch
                  checked={entryForm.billable}
                  onCheckedChange={(v) => setEntryForm({ ...entryForm, billable: v })}
                  data-testid="entry-billable-switch"
                />
                <span className="ml-2 text-sm">{entryForm.billable ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={entryForm.description}
              onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
              placeholder="Additional notes about this work..."
              rows={2}
              data-testid="entry-notes-input"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="entry-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="entry-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {editingEntry ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
